import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Contexto global de "renderização para exportação".
 *
 * Componentes paginados ou com scroll interno (ex.: PainelTabela) podem
 * consultar `useExportRender().renderAll` e renderizar TUDO sem paginação
 * quando esse valor for `true`. O `<BotaoExportacao>` ativa esse modo antes
 * de chamar html2canvas e o desativa logo após.
 */

const ExportRenderContext = createContext({
  renderAll: false,
  setRenderAll: () => {},
});

/**
 * Detecta se a página foi carregada com ?export=1. Usado pelos iframes
 * ocultos do agendador — quando true, o ExportRenderProvider inicia com
 * renderAll=true (todas as linhas desenhadas, sem paginação) e o restante
 * do app pode esconder UI não-essencial (toasts, animações).
 */
export function isExportModeFromQuery() {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).has("export");
  } catch {
    return false;
  }
}

export function ExportRenderProvider({ children }) {
  const [renderAll, setRenderAll] = useState(() => isExportModeFromQuery());

  // Sincroniza data-export-mode no body com a query string. Se ?export=1
  // está presente, ativa; senão, REMOVE (defensivo contra vazamento de
  // estado entre sessões — ex: aba que ficou com attr setado por engano
  // em uma captura anterior que falhou).
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (isExportModeFromQuery()) {
      document.body.setAttribute("data-export-mode", "true");
    } else if (document.body.getAttribute("data-export-mode") === "true") {
      // Só remove se conferimos que NÃO há captura ativa rolando agora.
      // Capturas manuais (BotaoExportacao) setam e removem no try/finally;
      // se chegamos aqui via navegação normal, é seguro limpar.
      // Aguardamos 1 frame para não conflitar com captureWithExpand em curso.
      const id = setTimeout(() => {
        if (!isExportModeFromQuery()) {
          document.body.removeAttribute("data-export-mode");
        }
      }, 0);
      return () => clearTimeout(id);
    }
    return undefined;
  }, []);

  const value = useMemo(
    () => ({ renderAll, setRenderAll }),
    [renderAll]
  );
  return <ExportRenderContext.Provider value={value}>{children}</ExportRenderContext.Provider>;
}

export function useExportRender() {
  return useContext(ExportRenderContext);
}

/**
 * Hook auxiliar: devolve callbacks { beforeCapture, afterCapture } prontos
 * para passar ao `captureWithExpand` / `salvarImagem`.
 */
export function useExportRenderHooks() {
  const { setRenderAll } = useExportRender();
  const beforeCapture = useCallback(async () => {
    setRenderAll(true);
  }, [setRenderAll]);
  const afterCapture = useCallback(async () => {
    setRenderAll(false);
  }, [setRenderAll]);
  return useMemo(
    () => ({ beforeCapture, afterCapture }),
    [beforeCapture, afterCapture]
  );
}
